import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Avatar,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
  Link,
  Stack,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../services/api';
import toast from 'react-hot-toast';

// VK периодически блокирует/выкусывает права у разных публичных client_id'ов:
// VK Admin (6121396) — полностью заблокирован, VK Android (2274003) — "incorrect app",
// Kate Mobile (2685278) — выдаёт токен, но без `like` (likes.add → code 3).
// Поэтому даём пользователю несколько вариантов; если один не сработал — пробуем
// следующий. scope=all (1073737727) запрашивает полный набор прав; VK сам решает
// что отдать.
const VK_OAUTH_SCOPE = 1073737727; // all
const VK_OAUTH_REDIRECT = 'https://oauth.vk.com/blank.html';

interface VkApp {
  id: string;
  clientId: string;
  name: string;
  hint: string;
}

// Список взят/выверен по vkhost.github.io — публичному реестру живых
// VK OAuth-клиентов (его держат в актуальном состоянии). Если все варианты ниже
// отдают "incorrect app" — открывайте сам vkhost и пробуйте оттуда: они чаще
// обновляют список под новые блокировки VK.
// Порядок — наиболее вероятный → наименее. На 28.05.2026 подтверждено
// проверкой `likes.isLiked` что нижеперечисленные client_id'ы НЕ выдают
// `like` юзер-токену: Kate Mobile (2685278), Звонки ВКонтакте (7793118),
// vk.com Web (6287487). Они остаются в списке только как ориентир «уже
// проверено — не даёт». Когда выяснится что какой-то из не-проверенных тоже
// не даёт — допишите note и опустите ниже.
const VK_APPS: VkApp[] = [
  {
    id: 'marusia',
    clientId: '6463690',
    name: 'Маруся',
    hint: 'VK-ассистент. Чаще остальных выдаёт полный набор прав, включая like. Начать с него',
  },
  {
    id: 'vk-connect',
    clientId: '7497650',
    name: 'VK Connect',
    hint: 'Системный клиент авторизации VK',
  },
  {
    id: 'vk-admin-ios',
    clientId: '5776857',
    name: 'VK Admin (iOS)',
    hint: 'iOS-версия Admin живёт дольше обычного VK Admin (тот полностью заблокирован)',
  },
  {
    id: 'sferum',
    clientId: '7556576',
    name: 'Сферум',
    hint: 'Образовательная платформа VK',
  },
  {
    id: 'sferum-android',
    clientId: '7598572',
    name: 'Сферум (Android)',
    hint: 'Альтернатива обычному Сферум',
  },
  {
    id: 'vk-mail',
    clientId: '7799655',
    name: 'VK Почта',
    hint: 'Резервный',
  },
  {
    id: 'mailru-mail',
    clientId: '7539952',
    name: 'Почта Mail.ru',
    hint: 'Резервный',
  },
  // ↓↓↓ Ниже — варианты, которые при проверке `likes.isLiked` уже отказали.
  // Бэкенд их отвергнет при попытке добавить — оставлены чтобы вы не пробовали
  // их повторно.
  {
    id: 'vk-calls',
    clientId: '7793118',
    name: 'Звонки ВКонтакте',
    hint: '⚠ Проверено: токен НЕ даёт права like',
  },
  {
    id: 'vk-com',
    clientId: '6287487',
    name: 'vk.com (Web)',
    hint: '⚠ Проверено: токен НЕ даёт права like',
  },
  {
    id: 'kate-mobile',
    clientId: '2685278',
    name: 'Kate Mobile',
    hint: '⚠ Проверено: стабильный, но БЕЗ права like',
  },
];

// vkhost.github.io — открытый сервис от сообщества, который держит актуальный
// список VK OAuth-приложений. Если ничего из VK_APPS не сработало — пользователю
// стоит открыть этот сервис: они быстрее реагируют на блокировки VK.
const VKHOST_URL = 'https://vkhost.github.io/';

function buildVkOAuthUrl(clientId: string): string {
  return (
    `https://oauth.vk.com/authorize` +
    `?client_id=${clientId}` +
    `&scope=${VK_OAUTH_SCOPE}` +
    `&redirect_uri=${encodeURIComponent(VK_OAUTH_REDIRECT)}` +
    `&display=page&response_type=token&revoke=1&v=5.131`
  );
}

// Если пользователь вставил весь URL вида
// https://oauth.vk.com/blank.html#access_token=vk1.a.XXX&expires_in=0&user_id=123
// — вытаскиваем оттуда access_token.
function extractTokenFromInput(raw: string): string {
  const trimmed = raw.trim();
  const match = trimmed.match(/access_token=([^&\s]+)/);
  return match ? match[1] : trimmed;
}

export default function AccountsPage() {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ accessToken: '', description: '' });

  const loadAccounts = async () => {
    try {
      const res = await api.get('/vk-accounts');
      setAccounts(res.data.data.accounts);
    } catch (err) {
      toast.error('Не удалось загрузить аккаунты');
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const handleAdd = async () => {
    const cleanToken = extractTokenFromInput(formData.accessToken);
    if (!cleanToken) {
      toast.error('Введите токен');
      return;
    }
    try {
      await api.post('/vk-accounts', {
        accessToken: cleanToken,
        description: formData.description,
      });
      toast.success('Аккаунт добавлен!');
      setOpen(false);
      setFormData({ accessToken: '', description: '' });
      loadAccounts();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Ошибка добавления');
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await api.post(`/vk-accounts/${id}/toggle`);
      toast.success('Статус изменён');
      loadAccounts();
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  // ---- Edit ----
  const [editAcc, setEditAcc] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({
    description: '',
    dailyLikeLimit: 100,
    dailyCommentLimit: 30,
    dailyFollowLimit: 50,
    minDelaySeconds: 30,
    maxDelaySeconds: 300,
  });
  const openEdit = (acc: any) => {
    setEditAcc(acc);
    setEditForm({
      description: acc.description ?? '',
      dailyLikeLimit: acc.dailyLikeLimit ?? 100,
      dailyCommentLimit: acc.dailyCommentLimit ?? 30,
      dailyFollowLimit: acc.dailyFollowLimit ?? 50,
      minDelaySeconds: acc.minDelaySeconds ?? 30,
      maxDelaySeconds: acc.maxDelaySeconds ?? 300,
    });
  };
  const closeEdit = () => setEditAcc(null);
  const submitEdit = async () => {
    if (!editAcc) return;
    if (editForm.minDelaySeconds >= editForm.maxDelaySeconds) {
      toast.error('Мин. задержка должна быть меньше макс.');
      return;
    }
    try {
      await api.patch(`/vk-accounts/${editAcc.id}`, editForm);
      toast.success('Сохранено');
      closeEdit();
      loadAccounts();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Ошибка');
    }
  };

  // ---- Delete ----
  const [deleteAcc, setDeleteAcc] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    if (!deleteAcc) return;
    setDeleting(true);
    try {
      await api.delete(`/vk-accounts/${deleteAcc.id}`);
      toast.success('Аккаунт удалён');
      setDeleteAcc(null);
      loadAccounts();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Ошибка удаления');
    } finally {
      setDeleting(false);
    }
  };

  const tokenLooksLikeUrl = useMemo(
    () => /access_token=/.test(formData.accessToken),
    [formData.accessToken]
  );

  const [customAppId, setCustomAppId] = useState('');
  const customAppIdValid = /^\d{5,}$/.test(customAppId.trim());

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Ссылка скопирована');
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">VK Аккаунты</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Добавить аккаунт
        </Button>
      </Box>

      <Grid container spacing={3}>
        {accounts.map((acc) => (
          <Grid item xs={12} md={6} key={acc.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar src={acc.photoUrl} sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6">
                      {acc.firstName} {acc.lastName}
                    </Typography>
                    <Chip
                      label={acc.isActive ? 'Активен' : 'Отключён'}
                      color={acc.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {acc.description}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption">
                    Лайки: {acc.todayLikes}/{acc.dailyLikeLimit}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(acc.todayLikes / acc.dailyLikeLimit) * 100}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption">
                    Комментарии: {acc.todayComments}/{acc.dailyCommentLimit}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(acc.todayComments / acc.dailyCommentLimit) * 100}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                {acc.tokenValid === false && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                    Токен сломан — нужна переавторизация
                  </Alert>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" onClick={() => handleToggle(acc.id)}>
                    {acc.isActive ? 'Отключить' : 'Включить'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => openEdit(acc)}
                  >
                    Изменить
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteAcc(acc)}
                  >
                    Удалить
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {accounts.length === 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" align="center">
                  Аккаунтов пока нет. Нажмите «Добавить аккаунт», чтобы привязать первый.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить VK аккаунт</DialogTitle>
        <DialogContent>
          <Accordion defaultExpanded sx={{ mb: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Как получить OAuth-токен ВКонтакте</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <Typography variant="body2">
                  <b>1.</b> Войдите в нужный VK-аккаунт в этом браузере (на vk.com).
                </Typography>
                <Typography variant="body2">
                  <b>2.</b> Откройте авторизацию через одно из приложений ниже. VK периодически
                  блокирует или меняет права у публичных client_id'ов, поэтому если первый вариант
                  отдаёт <code>incorrect app</code> или авторизация проходит, но потом действия
                  падают — пробуйте следующий:
                </Typography>

                <Stack spacing={1} sx={{ pl: 2 }}>
                  {VK_APPS.map((app) => {
                    const url = buildVkOAuthUrl(app.clientId);
                    return (
                      <Box
                        key={app.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5,
                          borderBottom: '1px dashed',
                          borderColor: 'divider',
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ minWidth: 180, justifyContent: 'flex-start' }}
                        >
                          {app.name}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          {app.hint}
                        </Typography>
                        <Tooltip title="Скопировать ссылку">
                          <IconButton size="small" onClick={() => copyUrl(url)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Stack>

                <Alert severity="info" sx={{ mt: 1 }}>
                  Если все варианты выше отдают <code>incorrect app</code> — откройте{' '}
                  <Link href={VKHOST_URL} target="_blank" rel="noopener noreferrer">
                    vkhost.github.io
                  </Link>
                  . Это открытый сервис от сообщества, в котором актуальный список рабочих VK-приложений
                  поддерживается оперативнее, чем здесь. Выбираете там приложение → жмёте «Получить» →
                  переходите по ссылке → копируете токен из адресной строки → вставляете сюда. Сервис
                  только формирует ссылку, ничего не сохраняет.
                </Alert>

                <Box sx={{ pl: 2, pt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Свой client_id (если создали standalone-приложение на{' '}
                    <Link
                      href="https://vk.com/apps?act=manage"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      vk.com/apps?act=manage
                    </Link>
                    ):
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <TextField
                      size="small"
                      placeholder="например: 51891234"
                      value={customAppId}
                      onChange={(e) =>
                        setCustomAppId(e.target.value.replace(/[^\d]/g, ''))
                      }
                      sx={{ maxWidth: 180 }}
                    />
                    <Button
                      component="a"
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      disabled={!customAppIdValid}
                      href={
                        customAppIdValid ? buildVkOAuthUrl(customAppId.trim()) : undefined
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Авторизоваться
                    </Button>
                    <Tooltip title="Скопировать ссылку">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!customAppIdValid}
                          onClick={() => copyUrl(buildVkOAuthUrl(customAppId.trim()))}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>

                <Typography variant="body2">
                  <b>3.</b> Нажмите «Разрешить». VK перебросит вас на «пустую» страницу{' '}
                  <code>oauth.vk.com/blank.html</code> — пугаться не нужно, токен лежит прямо в
                  адресной строке после <code>#access_token=</code>.
                </Typography>
                <Typography variant="body2">
                  <b>4.</b> Скопируйте всю строку из адресной строки (целиком, начиная с{' '}
                  <code>https://</code>) или только сам <code>access_token=...</code> до символа{' '}
                  <code>&amp;</code> — вставьте в поле ниже. Лишнее будет отрезано автоматически.
                </Typography>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <AlertTitle>Безопасность</AlertTitle>
                  Токен — это полный доступ к аккаунту в рамках запрошенных прав. Не передавайте его
                  посторонним. В нашей БД он шифруется AES-256-GCM. Отозвать выданный токен можно в
                  настройках VK:{' '}
                  <Link
                    href="https://vk.com/settings?act=apps"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    vk.com/settings?act=apps
                  </Link>
                  .
                </Alert>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 2 }} />

          <TextField
            fullWidth
            label="VK Access Token"
            placeholder="vk1.a.XXXXXXXX...   (или вставьте URL целиком — выдернем токен сами)"
            value={formData.accessToken}
            onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
            margin="normal"
            multiline
            minRows={2}
            helperText={
              tokenLooksLikeUrl
                ? 'Похоже на URL — токен будет автоматически извлечён при сохранении'
                : 'Можно вставить и сам токен (vk1.a.…), и весь URL из адресной строки после авторизации'
            }
          />
          <TextField
            fullWidth
            label="Описание (метка для себя)"
            placeholder="например: Основной аккаунт"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!formData.accessToken.trim()}>
            Добавить
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Edit dialog ===== */}
      <Dialog open={!!editAcc} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          Изменить аккаунт
          {editAcc && (
            <Typography variant="body2" color="text.secondary">
              {editAcc.firstName} {editAcc.lastName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Описание"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Дневные лимиты
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                type="number"
                label="Лайков в день"
                value={editForm.dailyLikeLimit}
                onChange={(e) =>
                  setEditForm({ ...editForm, dailyLikeLimit: Number(e.target.value) })
                }
                inputProps={{ min: 1, max: 1000 }}
              />
              <TextField
                fullWidth
                type="number"
                label="Комментариев в день"
                value={editForm.dailyCommentLimit}
                onChange={(e) =>
                  setEditForm({ ...editForm, dailyCommentLimit: Number(e.target.value) })
                }
                inputProps={{ min: 1, max: 500 }}
              />
              <TextField
                fullWidth
                type="number"
                label="Подписок в день"
                value={editForm.dailyFollowLimit}
                onChange={(e) =>
                  setEditForm({ ...editForm, dailyFollowLimit: Number(e.target.value) })
                }
                inputProps={{ min: 1, max: 500 }}
              />
            </Stack>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Задержка между действиями, секунды
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                type="number"
                label="Мин."
                value={editForm.minDelaySeconds}
                onChange={(e) =>
                  setEditForm({ ...editForm, minDelaySeconds: Number(e.target.value) })
                }
                inputProps={{ min: 10, max: 600 }}
                helperText="≥ 10"
              />
              <TextField
                fullWidth
                type="number"
                label="Макс."
                value={editForm.maxDelaySeconds}
                onChange={(e) =>
                  setEditForm({ ...editForm, maxDelaySeconds: Number(e.target.value) })
                }
                inputProps={{ min: 30, max: 3600 }}
                helperText="≤ 3600"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Отмена</Button>
          <Button variant="contained" onClick={submitEdit}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Delete confirm ===== */}
      <Dialog open={!!deleteAcc} onClose={() => setDeleteAcc(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Удалить аккаунт?
        </DialogTitle>
        <DialogContent>
          {deleteAcc && (
            <Stack spacing={1.5}>
              <Typography>
                Будет удалён аккаунт{' '}
                <b>
                  {deleteAcc.firstName} {deleteAcc.lastName}
                </b>{' '}
                и все его привязки: конфиги автолайкера/автокоммент/автоподписки, история действий.
              </Typography>
              <Alert severity="warning">
                Это действие необратимо. Зашифрованный VK-токен будет уничтожен.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAcc(null)} disabled={deleting}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleting}
            startIcon={<DeleteIcon />}
          >
            {deleting ? 'Удаляю…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
